/*
 * Lightweight logger utility that conditionally logs based on the current
 * Vite/Node environment. In non-development modes all methods are no-ops.
 */

// Adopt the Vite convention for exposing the current mode.
const isDevelopment = import.meta.env.MODE === 'development';

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

// General log helper – resolves to the correct console method.
/**
 * Creates a logging function for the specified severity level.
 *
 * In development mode, the returned function forwards all arguments to the
 * corresponding console method (console.log for debug, console.info for info,
 * etc.). In non-development modes, the returned function is a no-op to avoid
 * polluting production console output.
 *
 * @param level - The log severity level ("debug", "info", "warn", or "error").
 * @returns A variadic logging function that conditionally writes to the console.
 */
function createLogger(level: LogLevel) {
  return (...args: unknown[]): void => {
    if (!isDevelopment) {
      return;
    }

    switch (level) {
      case 'debug':
        console.log(...args);
        break;
      case 'info':
        console.info(...args);
        break;
      case 'warn':
        console.warn(...args);
        break;
      case 'error':
        console.error(...args);
        break;
      default:
        console.log(...args);
    }
  };
}

/**
 * Lightweight logger that conditionally logs based on the Vite environment.
 *
 * In non-development modes all methods are no-ops. Each method maps to the
 * corresponding console method (debug->log, info->info, warn->warn, error->error).
 */
export const logger = {
  debug: createLogger('debug'),
  info: createLogger('info'),
  warn: createLogger('warn'),
  error: createLogger('error'),
} as const;
